/**
 * Public Store Page
 *
 * Displays a user's public storefront with products.
 * Accessible at /s/:storeSlug
 */

import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getLanguageDirection, type LanguageCode } from '../../i18n/languages';
import StoreFooter from '../../components/storefront/StoreFooter';
import CookieConsent from '../../components/storefront/CookieConsent';

interface Store {
  id: string;
  name: string;
  slug: string;
  description?: string;
  logo_url?: string;
  banner_url?: string;
  theme: StoreTheme;
  currency: string;
  locale: LanguageCode;
  social_links?: Record<string, string>;
  contact_email?: string;
}

interface StoreTheme {
  primaryColor: string;
  secondaryColor: string;
  backgroundColor: string;
  textColor: string;
  fontFamily: string;
  headerStyle: 'standard' | 'centered' | 'minimal';
  productCardStyle: 'minimal' | 'detailed' | 'overlay';
}

interface Product {
  id: string;
  name: string;
  slug: string;
  short_description?: string;
  price: number;
  compare_at_price?: number;
  currency: string;
  images: string[];
  status: string;
  featured: boolean;
  categories: string[];
}

interface Category {
  id: string;
  name: string;
  slug: string;
  description?: string;
  image_url?: string;
}

const defaultTheme: StoreTheme = {
  primaryColor: '#0d9488',
  secondaryColor: '#6366f1',
  backgroundColor: '#ffffff',
  textColor: '#1f2937',
  fontFamily: 'Inter',
  headerStyle: 'standard',
  productCardStyle: 'detailed',
};

/**
 * Safely parse JSON from localStorage with fallback
 */
function safeParseJSON<T>(json: string | null, fallback: T): T {
  if (!json) return fallback;
  try {
    return JSON.parse(json) as T;
  } catch {
    console.warn('Failed to parse JSON from localStorage, using fallback');
    return fallback;
  }
}

export default function PublicStorePage() {
  const { storeSlug } = useParams<{ storeSlug: string }>();
  const { t, i18n } = useTranslation('store');
  const [store, setStore] = useState<Store | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [cartCount, setCartCount] = useState(0);

  // Change language when store locale is loaded
  useEffect(() => {
    if (store?.locale && i18n.language !== store.locale) {
      i18n.changeLanguage(store.locale);
    }
  }, [store?.locale, i18n]);

  useEffect(() => {
    async function loadStore() {
      try {
        setLoading(true);

        // Load store data
        const storeResponse = await fetch(`/api/public/stores/${storeSlug}`);
        if (!storeResponse.ok) {
          if (storeResponse.status === 404) {
            setError('Store not found');
          } else {
            setError('Failed to load store');
          }
          return;
        }
        const storeData = await storeResponse.json() as { store: Store; products: Product[]; categories: Category[] };

        setStore(storeData.store);
        setProducts(storeData.products || []);
        setCategories(storeData.categories || []);

        // Load cart count from localStorage
        const cartKey = `cart_${storeData.store.id}`;
        const cart = safeParseJSON<Array<{ quantity: number }>>(localStorage.getItem(cartKey), []);
        setCartCount(cart.reduce((sum: number, item: { quantity: number }) => sum + item.quantity, 0));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load store');
      } finally {
        setLoading(false);
      }
    }

    if (storeSlug) {
      loadStore();
    }
  }, [storeSlug]);

  const theme = store?.theme || defaultTheme;

  const filteredProducts = products.filter((product) => {
    const matchesSearch = !searchQuery ||
      product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.short_description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !selectedCategory || product.categories.includes(selectedCategory);
    return matchesSearch && matchesCategory;
  });

  const storeLocale = store?.locale || 'en';
  const storeDirection = getLanguageDirection(storeLocale);

  const formatPrice = (price: number, currency: string) => {
    // Map language code to locale format for Intl
    const localeMap: Record<string, string> = {
      en: 'en-US', zh: 'zh-CN', hi: 'hi-IN', es: 'es-ES', fr: 'fr-FR',
      ar: 'ar-SA', bn: 'bn-BD', pt: 'pt-BR', ru: 'ru-RU', ja: 'ja-JP',
      de: 'de-DE', ko: 'ko-KR', vi: 'vi-VN', tr: 'tr-TR', it: 'it-IT',
      th: 'th-TH', pl: 'pl-PL', nl: 'nl-NL', id: 'id-ID', he: 'he-IL',
    };
    const intlLocale = localeMap[storeLocale] || 'en-US';
    return new Intl.NumberFormat(intlLocale, {
      style: 'currency',
      currency: currency || 'USD',
    }).format(price / 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (error || !store) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('storefront.storeNotFound')}</h1>
          <p className="text-gray-500 mb-6">{t('storefront.storeNotFoundDescription')}</p>
          <Link to="/" className="text-teal-600 hover:text-teal-700 font-medium">
            {t('storefront.goToHomepage')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: theme.backgroundColor, color: theme.textColor }}
      dir={storeDirection}
      lang={storeLocale}
    >
        {/* Header */}
        <header className="sticky top-0 z-50 bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              {/* Logo/Name */}
              <Link to={`/s/${store.slug}`} className="flex items-center gap-3">
                {store.logo_url ? (
                  <img src={store.logo_url} alt={store.name} className="h-10 w-auto" />
                ) : (
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold"
                    style={{ backgroundColor: theme.primaryColor }}
                  >
                    {store.name.charAt(0).toUpperCase()}
                  </div>
                )}
                <span className="text-xl font-bold" style={{ color: theme.textColor }}>
                  {store.name}
                </span>
              </Link>

              {/* Search */}
              <div className="hidden md:flex flex-1 max-w-md mx-8">
                <div className="relative w-full">
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    type="text"
                    placeholder={t('storefront.searchProducts')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:border-transparent"
                    style={{ '--tw-ring-color': theme.primaryColor } as React.CSSProperties}
                  />
                </div>
              </div>

              {/* Cart */}
              <Link
                to={`/s/${store.slug}/cart`}
                className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
                {cartCount > 0 && (
                  <span
                    className="absolute -top-1 -right-1 w-5 h-5 text-xs text-white rounded-full flex items-center justify-center"
                    style={{ backgroundColor: theme.primaryColor }}
                  >
                    {cartCount}
                  </span>
                )}
              </Link>
            </div>
          </div>
        </header>

        {/* Banner */}
        {store.banner_url && (
          <div className="relative h-48 md:h-64 overflow-hidden">
            <img
              src={store.banner_url}
              alt={store.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <div className="max-w-7xl mx-auto">
                <h1 className="text-3xl font-bold text-white">{store.name}</h1>
                {store.description && (
                  <p className="text-white/80 mt-2 max-w-xl">{store.description}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar - Categories */}
            {categories.length > 0 && (
              <aside className="lg:w-64 flex-shrink-0">
                <div className="sticky top-24">
                  <h3 className="font-semibold mb-4" style={{ color: theme.textColor }}>{t('storefront.categories')}</h3>
                  <nav className="space-y-1">
                    <button
                      onClick={() => setSelectedCategory(null)}
                      className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                        !selectedCategory ? 'font-medium' : 'hover:bg-gray-100'
                      }`}
                      style={!selectedCategory ? { backgroundColor: `${theme.primaryColor}15`, color: theme.primaryColor } : {}}
                    >
                      {t('storefront.allProducts')}
                    </button>
                    {categories.map((category) => (
                      <button
                        key={category.id}
                        onClick={() => setSelectedCategory(category.id)}
                        className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                          selectedCategory === category.id ? 'font-medium' : 'hover:bg-gray-100'
                        }`}
                        style={selectedCategory === category.id ? { backgroundColor: `${theme.primaryColor}15`, color: theme.primaryColor } : {}}
                      >
                        {category.name}
                      </button>
                    ))}
                  </nav>
                </div>
              </aside>
            )}

            {/* Products Grid */}
            <div className="flex-1">
              {/* Mobile Search */}
              <div className="md:hidden mb-6">
                <div className="relative">
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    type="text"
                    placeholder={t('storefront.searchProducts')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Results Count */}
              <div className="flex items-center justify-between mb-6">
                <p className="text-sm text-gray-500">
                  {filteredProducts.length} {filteredProducts.length === 1 ? t('storefront.product') : t('storefront.products')}
                </p>
              </div>

              {/* Products */}
              {filteredProducts.length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{t('storefront.noProductsFound')}</h3>
                  <p className="text-gray-500">
                    {searchQuery ? t('storefront.tryDifferentSearch') : t('storefront.checkBackLater')}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4 md:gap-6">
                  {filteredProducts.map((product) => (
                    <Link
                      key={product.id}
                      to={`/s/${store.slug}/p/${product.slug}`}
                      className="group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow"
                    >
                      {/* Product Image */}
                      <div className="aspect-square bg-gray-100 relative overflow-hidden">
                        {product.images.length > 0 ? (
                          <img
                            src={product.images[0]}
                            alt={product.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <svg className="w-12 h-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                        )}
                        {product.compare_at_price && product.compare_at_price > product.price && (
                          <span
                            className="absolute top-2 left-2 px-2 py-1 text-xs font-medium text-white rounded"
                            style={{ backgroundColor: theme.primaryColor }}
                          >
                            {t('storefront.sale')}
                          </span>
                        )}
                      </div>

                      {/* Product Info */}
                      <div className="p-4">
                        <h3 className="font-medium text-gray-900 group-hover:text-teal-600 transition-colors line-clamp-2">
                          {product.name}
                        </h3>
                        {product.short_description && (
                          <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                            {product.short_description}
                          </p>
                        )}
                        <div className="mt-2 flex items-center gap-2">
                          <span className="text-lg font-bold" style={{ color: theme.primaryColor }}>
                            {formatPrice(product.price, product.currency)}
                          </span>
                          {product.compare_at_price && product.compare_at_price > product.price && (
                            <span className="text-sm text-gray-400 line-through">
                              {formatPrice(product.compare_at_price, product.currency)}
                            </span>
                          )}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </main>

        {/* Footer */}
        <StoreFooter store={store} direction={storeDirection} />

        {/* Cookie Consent */}
        <CookieConsent
          storeId={store.id}
          storeSlug={store.slug}
          primaryColor={theme.primaryColor}
        />
      </div>
  );
}
